import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera } from 'lucide-react';
import { AutoVideo } from './AutoVideo';
import { CarfaxBadge } from './CarfaxBadge';
import { DriveIcon, GearIcon, SpeedIcon, PlayIcon } from './SpecIcons';
import { PLACEHOLDER, coverOf, driveLabel, transLabel, fmtPrice, fmtKm } from './inventoryUtils';

const Spec = ({ icon, label }) => (
  <div className="flex flex-col items-center">{icon}<span className="text-[10px] text-slate-500">{label}</span></div>
);

export const InventoryCard = ({ v, en, priority }) => {
  const [loaded, setLoaded] = useState(false);
  const price = fmtPrice(v.price, en);
  const photos = v.images?.length || 0;
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl hover:shadow-green-600/10 transition-shadow duration-300 flex flex-col" data-testid={`inventory-card-${v.id}`}>
      <Link to={`/nos-vehicules/${v.id}`} className="block relative overflow-hidden bg-slate-100 group" style={{ aspectRatio: '3/2' }}>
        {!loaded && !v.video_url && <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-700 animate-pulse" />}
        {v.video_url ? (
          <AutoVideo src={v.video_url} poster={coverOf(v)} className="absolute inset-0 w-full h-full object-cover z-10" testid={`inventory-video-${v.id}`} />
        ) : (
          <img
            src={coverOf(v)}
            alt={`${v.year} ${v.make} ${v.model}`}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={(e) => { e.currentTarget.src = PLACEHOLDER; setLoaded(true); }}
            className={`absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        {v.video_url && (
          <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-0.5 rounded-full flex items-center text-[10px] font-medium z-20" data-testid={`video-badge-${v.id}`}>
            <PlayIcon />
          </div>
        )}
        {photos > 0 && (
          <div className="absolute bottom-2 left-2 z-20 bg-black/70 text-white px-2 py-1 rounded-lg flex items-center gap-1.5 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="h-3.5 w-3.5" /><span>{photos}</span>
          </div>
        )}
        {v.status === 'new_arrival' && (
          <span className="absolute top-2 right-2 z-20 bg-[#1a5c3b] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
            {en ? 'New arrival' : 'Nouvel arrivage'}
          </span>
        )}
      </Link>
      <div className="px-4 pt-3 pb-1">
        <h3 className="font-bold text-slate-900 text-sm sm:text-base leading-tight">{v.year} {v.make} {v.model}</h3>
        <p className="text-slate-400 text-[10px] mt-0.5 uppercase leading-tight line-clamp-2">{v.trim}{v.title ? ` | ${v.title}` : ''}</p>
        {v.carfax_badges?.length > 0 && <div className="mt-2"><CarfaxBadge v={v} en={en} className="h-7" testid={`carfax-badge-${v.id}`} /></div>}
      </div>
      <div className="px-4 py-2 border-t border-b border-slate-100">
        <div className="grid grid-cols-3 gap-2 text-center">
          <Spec icon={<DriveIcon />} label={driveLabel(v.drivetrain, en)} />
          <Spec icon={<GearIcon />} label={transLabel(v.transmission, en)} />
          <Spec icon={<SpeedIcon />} label={fmtKm(v.mileage, en)} />
        </div>
      </div>
      <div className="px-4 py-3 flex-1">
        <div className="flex items-center justify-between">
          <span className="font-bold text-slate-800">{en ? 'Selling price' : 'Prix de vente'}</span>
          {price ? (
            <span className="text-xl font-black text-[#1a5c3b]" data-testid={`inventory-price-${v.id}`}>*{price}</span>
          ) : (
            <span className="text-sm font-semibold text-slate-600" data-testid={`inventory-price-${v.id}`}>{en ? 'Call us' : 'Appelez-nous'}</span>
          )}
        </div>
      </div>
      <div className="px-4 pb-4">
        <Link to={`/nos-vehicules/${v.id}`} className="block w-full bg-slate-800 hover:bg-[#1a5c3b] text-white text-center py-3 rounded font-semibold text-sm uppercase tracking-wide transition-colors" data-testid={`vehicle-details-btn-${v.id}`}>
          {en ? 'View details' : 'Voir les détails'}
        </Link>
      </div>
    </div>
  );
};
